/**
 * Lecture directe des manettes PlayStation (DualShock 4, DualSense, DualSense Edge) par WebHID.
 *
 * Pourquoi : sur macOS, Chrome ne reçoit pas tous les boutons par l'API Gamepad (Options, Create, pavé tactile
 * parfois absents). WebHID lit les rapports bruts de la manette et ne perd rien.
 *
 * L'état rendu a la même forme que celui de l'API Gamepad (disposition « standard » : voir BTN dans
 * ControllerLive.jsx), l'écran peut donc l'utiliser tel quel. USB uniquement : les rapports Bluetooth ont
 * une autre structure, la manette reste alors lue par l'API Gamepad.
 *
 * L'autorisation se donne une fois dans l'onglet Manettes (même site) : le navigateur s'en souvient et l'écran
 * client ouvre alors la manette tout seul.
 */

export const PS_HID_OK = typeof navigator !== "undefined" && !!navigator.hid;

const VENDEUR_SONY = 0x054c;
const PRODUITS = {0x05c4: "ds4", 0x09cc: "ds4", 0x0ce6: "ds5", 0x0df2: "ds5"};

// Position des octets dans le rapport USB n° 1 (le numéro de rapport lui-même n'est pas dans les données).
const OCTETS = {
    ds4: {croix: 4, boutons: 5, systeme: 6, l2: 7, r2: 8},
    ds5: {croix: 7, boutons: 8, systeme: 9, l2: 4, r2: 5},
};

const hex4 = n => n.toString(16).padStart(4, "0");
const axe = octet => Math.round(((octet - 127.5) / 128) * 100) / 100;
const bit = (octet, masque) => ((octet & masque) !== 0 ? 1 : 0);

/**
 * Traduit un rapport d'entrée en état « standard ». Rend null pour un rapport qu'on ne sait pas lire.
 * @param {number} produit  identifiant produit USB
 * @param {DataView} data   données du rapport n° 1, sans le numéro de rapport
 */
export function lireRapport(produit, data) {
    const modele = PRODUITS[produit];
    // Les rapports Bluetooth « simplifiés » sont plus courts et rangés autrement : on les ignore.
    if (!modele || data.byteLength < 30) return null;

    const o = OCTETS[modele];
    const croix = data.getUint8(o.croix);
    const boutons = data.getUint8(o.boutons);
    const systeme = data.getUint8(o.systeme);
    const hat = croix & 0x0f; // 0 = haut, puis sens horaire ; 8 = rien

    const b = new Array(18).fill(0);
    b[0] = bit(croix, 0x20);    // croix
    b[1] = bit(croix, 0x40);    // rond
    b[2] = bit(croix, 0x10);    // carré
    b[3] = bit(croix, 0x80);    // triangle
    b[4] = bit(boutons, 0x01);  // L1
    b[5] = bit(boutons, 0x02);  // R1
    b[6] = Math.round((data.getUint8(o.l2) / 255) * 100) / 100;
    b[7] = Math.round((data.getUint8(o.r2) / 255) * 100) / 100;
    b[8] = bit(boutons, 0x10);  // Create / Share
    b[9] = bit(boutons, 0x20);  // Options
    b[10] = bit(boutons, 0x40); // L3
    b[11] = bit(boutons, 0x80); // R3
    b[12] = hat === 0 || hat === 1 || hat === 7 ? 1 : 0; // haut
    b[13] = hat === 3 || hat === 4 || hat === 5 ? 1 : 0; // bas
    b[14] = hat === 5 || hat === 6 || hat === 7 ? 1 : 0; // gauche
    b[15] = hat === 1 || hat === 2 || hat === 3 ? 1 : 0; // droite
    b[16] = bit(systeme, 0x01); // PS
    b[17] = bit(systeme, 0x02); // pavé tactile

    return {
        id: `Manette PlayStation (STANDARD GAMEPAD Vendor: ${hex4(VENDEUR_SONY)} Product: ${hex4(produit)})`,
        mapping: "standard",
        source: "hid",
        buttons: b,
        axes: [0, 1, 2, 3].map(i => axe(data.getUint8(i))),
    };
}

/**
 * Suit la première manette PlayStation déjà autorisée : appelle `quandChange(etat)` à chaque rapport
 * (etat = null quand la manette est débranchée). Rend la fonction qui arrête le suivi.
 */
export function suivreManettesPS(quandChange) {
    if (!PS_HID_OK) return () => {};

    let courant = null;
    let arrete = false;

    const surRapport = e => {
        if (e.reportId !== 1) return;
        const etat = lireRapport(e.device.productId, e.data);
        if (etat) quandChange(etat);
    };

    const lacher = () => {
        if (!courant) return;
        courant.removeEventListener("inputreport", surRapport);
        const ancien = courant;
        courant = null;
        ancien.close().catch(() => {});
        quandChange(null);
    };

    const ouvrir = async appareil => {
        if (arrete || courant || !PRODUITS[appareil.productId] || appareil.vendorId !== VENDEUR_SONY) return;
        try {
            if (!appareil.opened) await appareil.open();
        } catch (e) {
            return; // déjà ouverte ailleurs : l'API Gamepad prend le relais
        }
        if (arrete) {
            appareil.close().catch(() => {});
            return;
        }
        courant = appareil;
        appareil.addEventListener("inputreport", surRapport);
    };

    const chercher = () => navigator.hid.getDevices().then(liste => liste.forEach(ouvrir)).catch(() => {});

    const surConnexion = e => ouvrir(e.device);
    const surDeconnexion = e => {
        if (e.device === courant) lacher();
    };

    navigator.hid.addEventListener("connect", surConnexion);
    navigator.hid.addEventListener("disconnect", surDeconnexion);
    chercher();

    return () => {
        arrete = true;
        navigator.hid.removeEventListener("connect", surConnexion);
        navigator.hid.removeEventListener("disconnect", surDeconnexion);
        lacher();
    };
}
